import { API_ORIGIN } from "./bootstrap.js";
/**
 * ⭐ **GERÇEK KULLANICI ÖLÇÜMÜ (RUM)** — Rapor 14 §12, F4-12.
 *
 * ### ⚠️ Üçüncü taraf RUM kütüphanesi YOK
 * `web-vitals` 2–4 KB'dır ve kritik yol bütçesine (≤110 KB) girer. İhtiyacımız
 * olan üç metrik `PerformanceObserver` ile doğrudan okunabilir; kütüphane
 * çoğunlukla eski tarayıcı yedeklerini taşır ve bizim hedef matrisimizde
 * (Rapor 12 §7) hepsi yerel olarak desteklenir.
 *
 * ### ⚠️ Ölçüm **görüşmeyi etkilemez**
 * Gönderim `sendBeacon` ile yapılır ve **yalnız sayfa gizlendiğinde** bir kez
 * çalışır. Periyodik gönderim, medya akarken ana iş parçacığını ve ağı boşuna
 * meşgul ederdi.
 *
 * ### ⚠️ PII TAŞINMAZ
 * Yalnız sayısal metrikler ve rota **şablonu** (`/room/:id`) gider; gerçek
 * toplantı kimliği ve davet sırrı **hiçbir zaman** telemetriye girmez
 * (Rapor 14 §9.3).
 */

export interface RumSample {
  /** Largest Contentful Paint (ms). */
  lcp: number | null;
  /** Interaction to Next Paint (ms) — INP, 2024'ten beri Core Web Vital. */
  inp: number | null;
  /** Cumulative Layout Shift (birimsiz). */
  cls: number | null;
  /** Katılım → ilk uzak kare (ms). ⭐ Bu ürünün ASIL metriği (F1-33). */
  timeToFirstFrame: number | null;
  route: string;
}

/**
 * ⚠️ Rota **şablona** indirgenir: `/room/7f3a…` yerine `/room/:id`.
 *    Ham yol telemetriye girerse toplantı kimlikleri log'lara yayılır.
 */
export function routeTemplate(pathname: string): string {
  return pathname
    .replace(/\/j\/[0-9a-f]{32}/, "/j/:id")
    .replace(/\/room\/[0-9a-f]{32}/, "/room/:id");
}

/**
 * Performans bütçeleri — Rapor 14 §12.
 *
 * ⚠️ Eşikler **tek yerde**: CI (`size-limit`, Lighthouse) ve RUM aynı sayıları
 *    kullanmalıdır. İki yere yazılırsa biri güncellenir, diğeri unutulur ve
 *    "bütçe geçti" raporu yalan olur.
 */
export const PERF_BUDGETS = Object.freeze({
  /** Google "iyi" eşiği. */
  lcpMs: 2_500,
  inpMs: 200,
  cls: 0.1,
  /** ⭐ Ürün hedefi: "Katıl" → ilk uzak kare <3 sn (p75) — Rapor 15 F1-33. */
  timeToFirstFrameMs: 3_000,
});

export type BudgetName = keyof typeof PERF_BUDGETS;

export interface BudgetBreach {
  budget: BudgetName;
  value: number;
  threshold: number;
}

/**
 * Bütçe ihlallerini bulur.
 *
 * ⚠️ `null` metrik **ihlal DEĞİLDİR**: tarayıcı desteklemiyorsa veya sayfa
 *    hiç etkileşim almadıysa metrik yoktur. `null`'ı 0 saymak "her şey
 *    mükemmel", sonsuz saymak "her şey bozuk" derdi; ikisi de yanlış.
 */
export function findBreaches(sample: RumSample): BudgetBreach[] {
  const out: BudgetBreach[] = [];
  const check = (name: BudgetName, value: number | null): void => {
    if (value === null) return;
    const threshold = PERF_BUDGETS[name];
    if (value > threshold) out.push({ budget: name, value, threshold });
  };

  check("lcpMs", sample.lcp);
  check("inpMs", sample.inp);
  check("cls", sample.cls);
  check("timeToFirstFrameMs", sample.timeToFirstFrame);
  return out;
}

/**
 * RUM toplayıcısı.
 *
 * ⚠️ `PerformanceObserver` **desteklenmiyorsa sessizce hiçbir şey yapmaz**:
 *    ölçüm bir yan üründür ve yokluğu uygulamayı düşürmemelidir.
 */
/**
 * 🚨 **RUM İSTEMCİ TELEMETRİSİ HİÇBİR ZAMAN SUNUCUYA ULAŞMADI.**
 *
 * Varsayılan adres **göreliydi** (`/api/v1/telemetry/rum`). İstemci Pages'te
 * olduğu için `sendBeacon` isteği `centvox-frontend.pages.dev`'e gidiyor, orası
 * yalnız statik dosya sunduğu için **405 Method Not Allowed** dönüyordu.
 * Uç sunucuda vardı ve çalışıyordu; kimse ona ulaşmıyordu.
 *
 * ⚠️ `beacon` sessizdir: hata yutulur (`catch {}`), gönderim sayfa kapanırken
 *    yapılır ve kimse konsola bakmaz. Bu yüzden aylarca fark edilmeyebilirdi —
 *    ve ölçmediğimiz için "veri yok"u "sorun yok" sanmaya devam ederdik.
 */
export function startRum(endpoint = `${API_ORIGIN}/api/v1/telemetry/rum`): () => void {
  if (typeof PerformanceObserver === "undefined") return () => undefined;

  const sample: RumSample = {
    lcp: null,
    inp: null,
    cls: null,
    timeToFirstFrame: null,
    route: routeTemplate(location.pathname),
  };

  const observers: PerformanceObserver[] = [];

  const observe = (type: string, cb: (entries: PerformanceEntryList) => void): void => {
    try {
      const po = new PerformanceObserver((list) => cb(list.getEntries()));
      // ⚠️ `buffered: true`: gözlemci kurulmadan ÖNCE olan girdiler de gelir.
      //    Olmadan LCP neredeyse her zaman kaçırılır (sayfa yüklenirken olur).
      po.observe({ type, buffered: true });
      observers.push(po);
    } catch {
      // Tarayıcı bu tipi bilmiyor — ölçüm atlanır, hata üretilmez.
    }
  };

  observe("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1];
    if (last) sample.lcp = Math.round(last.startTime);
  });

  observe("event", (entries) => {
    for (const e of entries) {
      const dur = (e as PerformanceEntry & { duration: number }).duration;
      // ⚠️ INP **en kötü** etkileşimdir, ortalama DEĞİL: kullanıcı ortalamayı
      //    değil, takılan tıklamayı hatırlar.
      if (sample.inp === null || dur > sample.inp) sample.inp = Math.round(dur);
    }
  });

  observe("layout-shift", (entries) => {
    for (const e of entries) {
      const shift = e as PerformanceEntry & { value: number; hadRecentInput: boolean };
      // ⚠️ Kullanıcı etkileşiminden hemen sonraki kayma sayılmaz (beklenen).
      if (shift.hadRecentInput) continue;
      sample.cls = (sample.cls ?? 0) + shift.value;
    }
  });

  const send = (): void => {
    /**
     * ⭐ TTFF **gönderim anında** okunur (F4-12).
     *
     * ⚠️ Gözlemci kurulurken okunamaz: `startRum()` uygulama açılışında
     *    çalışır, ilk uzak kare ise dakikalar sonra gelebilir. Önceden
     *    okumak alanı **her zaman `null`** bırakırdı — denetimin bulduğu
     *    durum tam olarak buydu.
     */
    sample.timeToFirstFrame = readTimeToFirstFrame();

    // ⚠️ `sendBeacon`: sayfa kapanırken `fetch` iptal edilir, beacon edilmez.
    try {
      navigator.sendBeacon(endpoint, JSON.stringify({ ...sample, breaches: findBreaches(sample) }));
    } catch {
      /* telemetri hiçbir zaman uygulamayı düşürmez */
    }
  };

  /**
   * ⚠️ `visibilitychange` kullanılır, `beforeunload` DEĞİL: mobil tarayıcılar
   *    `beforeunload`u sık sık hiç tetiklemez (uygulama arka plana alınır ve
   *    öldürülür) ve ölçüm kaybolur.
   */
  const onHidden = (): void => {
    if (document.visibilityState === "hidden") send();
  };
  document.addEventListener("visibilitychange", onHidden);

  return () => {
    document.removeEventListener("visibilitychange", onHidden);
    for (const po of observers) po.disconnect();
  };
}

export const JOIN_MARK = "centvox:join-start";
export const FIRST_FRAME_MARK = "centvox:first-remote-frame";

/**
 * ⭐ **KENDİ YAYININ AÇILDIĞI AN** — "kendi görüntüm geç geldi"nin ölçüsü.
 *
 * 🚨 Bu ölçüm **hiç yoktu**. TTFF yalnız *uzak* kareyi ölçer; kullanıcının
 *    kendi kamerasının ne zaman açıldığı hiçbir yerde kaydedilmiyordu. Canlı
 *    kayıtta (30 Tem 2026) host *"görüntüm ve kameram geç geldi"* dedi ve
 *    elimizde yalnız sunucu kaydından çıkarılan dolaylı bir sayı vardı: bilet
 *    17:43:34, yayın 17:43:42 — **8 saniye**. Nerede geçtiği ise tahmine
 *    kalıyordu.
 *
 * ⚠️ Zincir bilinçli olarak SERİdir: `waitForMe()` (yetenek kapısı) →
 *    `setEffects()` → `getUserMedia()` → `startPublish()`. Hangisinin pahalı
 *    olduğunu bilmeden hızlandırmaya çalışmak, yetki kapısını donanımdan önce
 *    çalıştırma kararını da körlemesine bozma riski taşır. Önce ÖLÇ.
 */
export const LOCAL_MEDIA_MARK = "centvox:local-media-ready";

/**
 * ⭐ TTFF sayacının **başlangıcı** — odaya katılma anı.
 *
 * ⚠️ Bu işaret olmadan ölçüm sayfa yüklemesinden itibaren yapılırdı ve green
 *    room'da bir dakika bekleyen kullanıcı 60 sn+ TTFF üretirdi. Ölçülmek
 *    istenen şey ağ/SFU gecikmesidir, kullanıcının düşünme süresi değil.
 */
export function markJoinStart(): void {
  try {
    performance.mark(JOIN_MARK);
  } catch {
    /* ölçüm yan üründür */
  }
}

/**
 * ⭐ **Katılım → ilk uzak kare** işareti (F1-33).
 *
 * ⚠️ Bu, Core Web Vitals'ın ölçmediği ve bu ürün için **en önemli** metriktir:
 *    LCP mükemmel olabilir ama kullanıcı 8 saniye boyunca kimseyi görmezse
 *    ürün yavaştır.
 */
export function markFirstRemoteFrame(): void {
  try {
    performance.mark(FIRST_FRAME_MARK);
  } catch {
    /* ölçüm yan üründür */
  }
}

/**
 * İki işaret arasındaki **fark**.
 *
 * ⚠️ Katılım işareti yoksa `null` döner, ham `startTime` DEĞİL: "sayfa
 *    açılışından ilk kareye" farklı bir metriktir ve aynı alana yazılırsa
 *    p75 hesabı iki farklı ölçümün karışımı olur — sessizce yanlış bir sayı,
 *    eksik bir sayıdan kötüdür.
 */
export function readTimeToFirstFrame(): number | null {
  return spanFrom(JOIN_MARK, FIRST_FRAME_MARK);
}

/** ⭐ Katılım → **kendi** medyamız yayına girdi (bkz. `LOCAL_MEDIA_MARK`). */
export function markLocalMediaReady(): void {
  try {
    performance.mark(LOCAL_MEDIA_MARK);
  } catch {
    /* ölçüm yan üründür */
  }
}

/** ⭐ `markLocalMediaReady` ile katılım arasındaki fark. */
export function readTimeToLocalMedia(): number | null {
  return spanFrom(JOIN_MARK, LOCAL_MEDIA_MARK);
}

/**
 * İki işaret arasındaki **fark**.
 *
 * ⚠️ Katılım işareti yoksa `null` döner, ham `startTime` DEĞİL: "sayfa
 *    açılışından ilk kareye" farklı bir metriktir ve aynı alana yazılırsa
 *    p75 hesabı iki farklı ölçümün karışımı olur — sessizce yanlış bir sayı,
 *    eksik bir sayıdan kötüdür.
 */
function spanFrom(startMark: string, endMark: string): number | null {
  try {
    const end = performance.getEntriesByName(endMark)[0];
    const start = performance.getEntriesByName(startMark)[0];
    if (!end || !start) return null;
    // ⚠️ 0'a sabitlenir: işaretler tersine dönerse (saat düzeltmesi) negatif
    //    bir süre bütçe karşılaştırmasını sessizce geçerdi.
    return Math.max(0, Math.round(end.startTime - start.startTime));
  } catch {
    return null;
  }
}
